import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Icon, Toaster } from '@matillion/component-library'

import { ActionCellTypes } from '../'

import { useDeleteSchedule, useGetPermissions } from 'api/hooks'
import EllipsesActionItem, {
  ThemeType
} from 'components/EllipsesActionItem/EllipsesActionItem'
import ConfirmCancelModal from 'components/Modal/ProjectExplorerModal'
import { QueryKey } from 'constants/endpoint'
import { AppRoutes } from 'constants/route'
import { useNavigate } from 'react-router-dom'
import { PermissionsType } from 'types'

const ActionCell: FunctionComponent<ActionCellTypes> = ({
  projectId,
  item
}) => {
  const { t } = useTranslation()
  const { makeToast, clearToasts } = Toaster.useToaster()
  const { mutateAsync, isLoading } = useDeleteSchedule()
  const queryClient = useQueryClient()
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const navigate = useNavigate()

  const onSwitchModalDeleteModal = () => setIsDeleteModalVisible((v) => !v)

  const { data: editPermission } = useGetPermissions(
    projectId,
    PermissionsType.EditSchedules
  )
  const { data: deletePermission } = useGetPermissions(
    projectId,
    PermissionsType.DeleteSchedules
  )

  const handleEditClick = () => {
    clearToasts()
    navigate(AppRoutes.getScheduleEdit(projectId, item.id))
  }

  const handleDeleteClick = () => {
    clearToasts()
    mutateAsync({ values: { id: item.id } })
      .then(async () => {
        clearToasts()
        makeToast({
          title: t('schedulesListing.delete.responseMessage.success', {
            scheduleName: item.name
          }),
          message: '',
          type: 'success'
        })

        await queryClient.invalidateQueries({
          queryKey: [QueryKey.SCHEDULES_LIST, projectId]
        })
      })
      .catch((_e) => {
        makeToast({
          title: t('schedulesListing.delete.responseMessage.error', {
            scheduleName: item.name
          }),
          message: '',
          type: 'error'
        })
      })
      .finally(() => {
        onSwitchModalDeleteModal()
      })
  }

  if (!editPermission?.result && !deletePermission?.result) {
    return null
  }

  return (
    <>
      <EllipsesActionItem
        actions={[
          ...(editPermission?.result
            ? [
                {
                  title: t('schedulesListing.actions.edit'),
                  action: handleEditClick,
                  icon: <Icon.Pencil />,
                  dataTestId: 'ellipses-action-edit-schedule'
                }
              ]
            : []),
          ...(deletePermission?.result
            ? [
                {
                  title: t('schedulesListing.actions.delete'),
                  action: onSwitchModalDeleteModal,
                  icon: <Icon.Trash />,
                  type: 'danger' as ThemeType,
                  dataTestId: 'ellipses-action-delete-schedule'
                }
              ]
            : [])
        ]}
      />

      {isDeleteModalVisible && (
        <ConfirmCancelModal
          onValidate={handleDeleteClick}
          onCancel={onSwitchModalDeleteModal}
          content={t('schedulesListing.delete.modal.content')}
          validate={t('schedulesListing.delete.modal.validate')}
          waiting={isLoading}
        />
      )}
    </>
  )
}

export default ActionCell
